import React, { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Users, FileText, Eye, CheckCircle, Plus, ExternalLink, Sparkles, Compass, Cpu, Key, Database, RefreshCw, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockProspects } from '../../shared/data/prospects';
import { getStoredKeys, saveKeys, APIKeyEntry } from '../../shared/services/aiOrchestrator';

export const AdminDashboard: React.FC = () => {
  const prospectsList = Object.values(mockProspects);
  const [activeAdminTab, setActiveAdminTab] = useState<'prospects' | 'ai_orchestrator'>('prospects');
  const [keysList, setKeysList] = useState<APIKeyEntry[]>(getStoredKeys());
  const [newKeyProvider, setNewKeyProvider] = useState<APIKeyEntry['provider']>('groq');
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [d1Synced, setD1Synced] = useState(false);

  const handleAddKey = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName || !newKeyValue) return;
    const created: APIKeyEntry = {
      id: `key_${Date.now()}`,
      provider: newKeyProvider,
      name: newKeyName,
      key: newKeyValue,
      status: 'ACTIVE',
      usageCount: 0,
      dailyQuota: 10000
    };
    const updated = [...keysList, created];
    setKeysList(updated);
    saveKeys(updated);
    setNewKeyName('');
    setNewKeyValue('');
  };

  const handleSyncD1 = () => {
    setD1Synced(true);
    setTimeout(() => setD1Synced(false), 3000);
  };

  return (
    <div className="space-y-8 font-sans">
      {/* Top Banner Notice */}
      <div className="p-4 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-900 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-indigo-600 shrink-0" />
          <span className="text-xs font-bold">
            Painel YouMachine 360º — Orquestrador de IA & Autoconstrutor de Propostas Ativo.
          </span>
        </div>
        <div className="flex items-center gap-2">
           <button 
              onClick={() => setActiveAdminTab(activeAdminTab === 'prospects' ? 'ai_orchestrator' : 'prospects')}
              className="px-3 py-1.5 rounded-xl bg-white border border-indigo-300 text-indigo-900 font-extrabold text-xs uppercase tracking-wider transition-colors shadow-sm"
           >
              {activeAdminTab === 'prospects' ? '⚙️ Orquestrador IA' : '📋 Prospects'}
           </button>
           <Link 
             to="/solucoes" 
             target="_blank"
             className="px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs uppercase tracking-wider transition-colors shadow-sm"
           >
             Ver Soluções
           </Link>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-black text-slate-900 tracking-tight">Painel Administrativo YouMachine</h1>
          <p className="text-xs md:text-sm text-slate-500 font-medium">
            Gestão de prospects, propostas e orquestrador de inteligência artificial com Cloudflare D1.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" target="_blank">
            <Button variant="outline" size="sm" icon={<ExternalLink className="w-4 h-4" />}>
              Ver Home Pública
            </Button>
          </Link>
        </div>
      </div>

      {/* Tabs Selection */}
      <div className="flex gap-3 border-b border-slate-200 pb-2">
         <button 
            onClick={() => setActiveAdminTab('prospects')}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors border ${activeAdminTab === 'prospects' ? 'bg-[#0F172A] text-white border-slate-900' : 'bg-slate-100 text-slate-700 border-slate-200'}`}
         >
            Prospects ({prospectsList.length})
         </button>
         <button 
            onClick={() => setActiveAdminTab('ai_orchestrator')}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors border flex items-center gap-2 ${activeAdminTab === 'ai_orchestrator' ? 'bg-[#2563EB] text-white border-blue-700' : 'bg-slate-100 text-slate-700 border-slate-200'}`}
         >
            <Cpu className="w-4 h-4" /> Orquestrador de IA & APIs ({keysList.length} Chaves)
         </button>
      </div>

      {/* TAB 1: PROSPECTS LIST */}
      {activeAdminTab === 'prospects' && (
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prospectsList.map((prospect) => (
               <div key={prospect.id} className="bg-white p-6 rounded-2xl border border-slate-200 flex flex-col justify-between space-y-4">
                  <div>
                     <span className="text-[10px] font-black uppercase text-indigo-600 bg-indigo-50 px-2 py-1 rounded">{prospect.segment}</span>
                     <h3 className="font-heading font-bold text-lg text-slate-900 mt-2">{prospect.trade_name}</h3>
                     <p className="text-xs text-slate-500">{prospect.city}/{prospect.state}</p>
                  </div>
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                     <Link to={`/${prospect.slug}`} target="_blank" className="text-xs font-bold text-indigo-600 hover:underline">
                        Ver Proposta Comercial →
                     </Link>
                     <Link to={`/${prospect.slug}/proposta-canvas`} target="_blank" className="text-xs font-bold text-emerald-600 hover:underline">
                        Proposta Canvas IA
                     </Link>
                  </div>
               </div>
            ))}
         </div>
      )}

      {/* TAB 2: AI ORCHESTRATOR & API KEY POOL */}
      {activeAdminTab === 'ai_orchestrator' && (
         <div className="space-y-8">
            
            {/* Overview & Cloudflare D1 Status */}
            <div className="bg-slate-900 text-white p-8 rounded-2xl border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-6">
               <div className="space-y-2">
                  <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase tracking-widest">
                     <Database className="w-4 h-4" /> Cloudflare D1 Vault Sincronizado
                  </div>
                  <h3 className="text-2xl font-heading font-black">Orquestrador Inteligente de Chaves API</h3>
                  <p className="text-xs text-slate-300 font-light max-w-xl">
                     O sistema seleciona automaticamente a melhor chave disponível (Groq, OpenRouter, Gemini, Firecrawl). Se uma chave exceder a cota diária ou retornar erro `429 Rate Limit`, o orquestrador faz o fallback instantâneo sem interromper o usuário.
                  </p>
               </div>

               <button 
                  onClick={handleSyncD1}
                  className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 px-6 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-colors whitespace-nowrap"
               >
                  <RefreshCw className="w-4 h-4" /> {d1Synced ? '✓ Sincronizado com D1!' : 'Sincronizar Cloudflare D1'}
               </button>
            </div>

            {/* Form de Adição de Nova Chave */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 space-y-4">
               <h4 className="font-heading font-black text-sm uppercase text-[#0F172A]">Adicionar Nova Chave ao Pool de Provedores</h4>
               <form onSubmit={handleAddKey} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <select 
                     value={newKeyProvider} 
                     onChange={e => setNewKeyProvider(e.target.value as any)}
                     className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none text-slate-700"
                  >
                     <option value="groq">Groq AI (Ultra Fast)</option>
                     <option value="openrouter">OpenRouter (Multi Model)</option>
                     <option value="gemini">Google Gemini Flash</option>
                     <option value="firecrawl">Firecrawl (Web Scraper)</option>
                     <option value="steel">Steel.dev (Browser Automation)</option>
                  </select>
                  <input 
                     type="text" 
                     placeholder="Nome de Identificação (ex: Groq Key #3)" 
                     value={newKeyName}
                     onChange={e => setNewKeyName(e.target.value)}
                     className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none"
                  />
                  <input 
                     type="password" 
                     placeholder="Chave API (sk-... / gsk-...)" 
                     value={newKeyValue}
                     onChange={e => setNewKeyValue(e.target.value)}
                     className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none"
                  />
                  <button type="submit" className="bg-[#2563EB] text-white py-3 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-blue-700 transition-colors">
                     + Cadastrar Chave API
                  </button>
               </form>
            </div>

            {/* List de Chaves Ativas no Pool */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 space-y-4">
               <h4 className="font-heading font-black text-sm uppercase text-[#0F172A]">Pool de Chaves API Ativas</h4>
               <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                     <thead className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider">
                        <tr>
                           <th className="p-3">Provedor</th>
                           <th className="p-3">Identificação</th>
                           <th className="p-3">Chave Ocultada</th>
                           <th className="p-3">Uso / Requisições</th>
                           <th className="p-3">Status Orquestrador</th>
                        </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-200">
                        {keysList.map((k) => (
                           <tr key={k.id} className="hover:bg-slate-50">
                              <td className="p-3 font-bold uppercase text-[#2563EB]">{k.provider}</td>
                              <td className="p-3 font-bold text-[#0F172A]">{k.name}</td>
                              <td className="p-3 font-mono text-slate-400">{k.key.substring(0, 8)}••••••••</td>
                              <td className="p-3 font-bold text-slate-700">{k.usageCount} chamadas</td>
                              <td className="p-3">
                                 <span className="bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase px-2.5 py-1 rounded-md border border-emerald-300">
                                    ✓ {k.status}
                                 </span>
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
            </div>

         </div>
      )}

    </div>
  );
};
